import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  IconButton,
  Paper,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogActions,
  Autocomplete,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  CardMembership as CardIcon,
  People as PeopleIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import axios from "axios";

const UserPassesComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [usersWithPasses, setUsersWithPasses] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isUserListVisible, setIsUserListVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingPass, setEditingPass] = useState(null);
  const [editPassDialogOpen, setEditPassDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // State variables for adding pass
  const [passTypes, setPassTypes] = useState([]);
  const [lessonCategories, setLessonCategories] = useState([]);
  const [selectedPassType, setSelectedPassType] = useState(null);
  const [lessonCategoryCounts, setLessonCategoryCounts] = useState({});
  const [addPassDialogOpen, setAddPassDialogOpen] = useState(false);
  const [addPassError, setAddPassError] = useState("");
  const [addPassSuccess, setAddPassSuccess] = useState("");
  const [isAddingPass, setIsAddingPass] = useState(false);

  // NEW: state for "payed" checkbox (defaults to true)
  const [payedValue, setPayedValue] = useState(true);

  // State variables for duration
  const [durationValue, setDurationValue] = useState("");
  const [durationUnit, setDurationUnit] = useState("days");

  // Month availability state
  const [monthRestricted, setMonthRestricted] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);

  // Purchase period availability state
  const [purchasePeriodRestricted, setPurchasePeriodRestricted] = useState(false);
  const [purchaseAvailableFrom, setPurchaseAvailableFrom] = useState("");
  const [purchaseAvailableUntil, setPurchaseAvailableUntil] = useState("");

  // State variable for filtering users with at least one paid pass
  const [showPaidUsersOnly, setShowPaidUsersOnly] = useState(false);

  // New state variables
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserForPass, setSelectedUserForPass] = useState(null);

  // State to track if duration fields should be hidden due to pass type constraints
  const [shouldHideDuration, setShouldHideDuration] = useState(false);

  useEffect(() => {
    fetchPassTypesAndLessonCategories();
  }, []);

  // Helper function to determine if duration fields should be hidden
  const shouldHideDurationFields = (passType) => {
    if (!passType) return false;

    // Hide duration fields if pass type has month restrictions or purchase period restrictions
    return passType.month_restricted || passType.purchase_period_restricted;
  };

  const fetchPassTypesAndLessonCategories = async () => {
    try {
      const [passTypeResponse, lessonCategoryResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/`, {
          headers: { "Access-Token": process.env.REACT_APP_API_TOKEN },
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-category/`, {
          headers: { "Access-Token": process.env.REACT_APP_API_TOKEN },
        }),
      ]);

      if (
        passTypeResponse.status === 200 &&
        Array.isArray(passTypeResponse.data.objects)
      ) {
        setPassTypes(passTypeResponse.data.objects);
      } else {
        throw new Error("Failed to fetch pass types");
      }

      if (
        lessonCategoryResponse.status === 200 &&
        Array.isArray(lessonCategoryResponse.data.objects)
      ) {
        setLessonCategories(lessonCategoryResponse.data.objects);
      } else {
        throw new Error("Failed to fetch lesson categories");
      }
    } catch (error) {
      console.error("Error fetching pass types and lesson categories:", error);
    }
  };

  const handleUserPassesClick = async () => {
    try {
      setIsUserListVisible((prev) => !prev);

      if (usersWithPasses.length === 0) {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/user/passes`,
          { headers: { "Access-Token": process.env.REACT_APP_API_TOKEN } }
        );

        const data = response.data.users;

        // Consolidate passes for users with the same user_id
        const usersMap = {};

        data.forEach((entry) => {
          const fullName = entry.full_name;
          const passes = entry.passes; // 'passes' is an array
          const email = entry.email;

          // Assuming all passes in 'entry.passes' belong to the same user
          const userId = passes[0]?.user_id;

          if (!usersMap[userId]) {
            usersMap[userId] = {
              user_id: userId,
              full_name: fullName,
              email: email,
              passes: [],
            };
          }

          usersMap[userId].passes = usersMap[userId].passes.concat(passes);
        });

        const usersArray = Object.values(usersMap);
        setUsersWithPasses(usersArray);
        setLoading(false);
      }
    } catch (error) {
      console.error("Failed to fetch users with active passes:", error);
      setLoading(false);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleEditPass = (pass) => {
    setEditingPass(pass);
    setEditPassDialogOpen(true);
  };

  const handleDeletePass = (pass) => {
    if (window.confirm("Czy na pewno chcesz usunąć ten karnet?")) {
      deletePass(pass.id);
    }
  };

  const deletePass = async (passId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/pass/${passId}`,
        {
          headers: { "Access-Token": process.env.REACT_APP_API_TOKEN },
        }
      );
      // Remove the deleted pass from the selectedUser.passes
      setSelectedUser((prev) => ({
        ...prev,
        passes: prev.passes.filter((pass) => pass.id !== passId),
      }));
      // Update usersWithPasses state
      setUsersWithPasses((prevUsers) =>
        prevUsers.map((user) =>
          user.user_id === selectedUser.user_id
            ? {
              ...user,
              passes: user.passes.filter((pass) => pass.id !== passId),
            }
            : user
        )
      );
    } catch (error) {
      console.error("Failed to delete pass:", error);
    }
  };

  const handleSaveEditPass = async () => {
    try {
      setIsUpdating(true);

      // Create a copy of editingPass to modify
      const passToUpdate = { ...editingPass };

      // Convert valid_until to ISO datetime format if it exists
      if (passToUpdate.valid_until) {
        const date = new Date(passToUpdate.valid_until);
        passToUpdate.valid_until = date.toISOString();
      }

      await axios.put(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/pass/${editingPass.id}`,
        passToUpdate,
        {
          headers: { "Access-Token": process.env.REACT_APP_API_TOKEN },
        }
      );

      // Update the selectedUser.passes with the edited pass
      setSelectedUser((prev) => ({
        ...prev,
        passes: prev.passes.map((pass) =>
          pass.id === editingPass.id ? { ...editingPass } : pass
        ),
      }));

      // Update usersWithPasses state
      setUsersWithPasses((prevUsers) =>
        prevUsers.map((user) =>
          user.user_id === selectedUser.user_id
            ? {
              ...user,
              passes: user.passes.map((pass) =>
                pass.id === editingPass.id ? { ...editingPass } : pass
              ),
            }
            : user
        )
      );

      setEditPassDialogOpen(false);
      setEditingPass(null);
      setIsUpdating(false);
    } catch (error) {
      console.error("Failed to update pass:", error);
      setIsUpdating(false);
    }
  };

  const handlePassTypeChange = (event) => {
    const selectedPass = event.target.value;
    setSelectedPassType(selectedPass);

    // Reset lessons count for new pass type
    const newLessonsCount = {};
    selectedPass.lesson_categories_for_pass_type.forEach((category) => {
      newLessonsCount[category.lesson_category_id] = category.lessons_to_use;
    });
    setLessonCategoryCounts(newLessonsCount);

    // Check if duration fields should be hidden for this pass type
    const hideDuration = shouldHideDurationFields(selectedPass);
    setShouldHideDuration(hideDuration);

    // Set default duration from backend only if duration fields are not hidden
    if (!hideDuration && selectedPass.valid_duration) {
      // valid_duration comes as total seconds (float) from FastAPI/Pydantic
      const durationSeconds = selectedPass.valid_duration;
      const days = Math.round(durationSeconds / (24 * 60 * 60));

      if (days > 0) {
        if (days % 7 === 0) {
          // If divisible by 7, show as weeks
          setDurationValue((days / 7).toString());
          setDurationUnit("weeks");
        } else {
          // Otherwise show as days
          setDurationValue(days.toString());
          setDurationUnit("days");
        }
      }
    } else if (hideDuration) {
      // Clear duration values when hiding fields
      setDurationValue("");
      setDurationUnit("days");
    }
  };

  const handleLessonCountChange = (lessonCategoryId, count) => {
    setLessonCategoryCounts((prevState) => ({
      ...prevState,
      [lessonCategoryId]: count,
    }));
  };

  const handleMonthToggle = (monthNumber) => {
    setAvailableMonths(prev =>
      prev.includes(monthNumber)
        ? prev.filter(m => m !== monthNumber)
        : [...prev, monthNumber]
    );
  };

  const monthNames = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];

  const getLessonCategoryName = (categoryId) => {
    const category = lessonCategories.find((cat) => cat.id === categoryId);
    return category ? category.name : `ID: ${categoryId}`;
  };

  const handleAddPass = async () => {
    setIsAddingPass(true);
    setAddPassError("");
    setAddPassSuccess("");

    try {
      // Skip duration validation if fields are hidden (auto-calculated by backend)
      if (!shouldHideDuration && (!durationValue || parseInt(durationValue, 10) <= 0)) {
        setAddPassError("Proszę wprowadzić poprawną długość trwania.");
        setIsAddingPass(false);
        return;
      }

      // Validate month restriction
      if (monthRestricted && availableMonths.length === 0) {
        setAddPassError("Proszę wybrać co najmniej jeden miesiąc dla karnetów z ograniczeniem miesięcznym.");
        setIsAddingPass(false);
        return;
      }

      // Validate purchase period restriction
      if (purchasePeriodRestricted) {
        if (!purchaseAvailableFrom && !purchaseAvailableUntil) {
          setAddPassError("Proszę określić okres dostępności zakupu.");
          setIsAddingPass(false);
          return;
        }
        if (purchaseAvailableFrom && purchaseAvailableUntil &&
            new Date(purchaseAvailableFrom) >= new Date(purchaseAvailableUntil)) {
          setAddPassError("Data rozpoczęcia musi być wcześniejsza niż data zakończenia.");
          setIsAddingPass(false);
          return;
        }
      }

      const lessonsCount = selectedPassType.lesson_categories_for_pass_type.map((category) => ({
        lesson_category_id: category.lesson_category_id,
        lessons_to_use: category.is_unlimited ? 0 : parseInt(lessonCategoryCounts[category.lesson_category_id] || category.lessons_to_use, 10),
        is_unlimited: category.is_unlimited || false,
      }));

      const valid_from = new Date(); // Current date and time
      const valid_from_iso = valid_from.toISOString();

      // Calculate valid_until only if duration fields are visible (not auto-calculated)
      let valid_until_iso = null;

      if (!shouldHideDuration) {
        // Manual duration calculation when fields are visible
        let valid_until = new Date(valid_from);
        const durationVal = parseInt(durationValue, 10);

        if (durationUnit === "days") {
          valid_until.setDate(valid_until.getDate() + durationVal);
        } else if (durationUnit === "weeks") {
          valid_until.setDate(valid_until.getDate() + durationVal * 7);
        } else if (durationUnit === "months") {
          valid_until.setMonth(valid_until.getMonth() + durationVal);
        }

        valid_until_iso = valid_until.toISOString();
      }
      // If shouldHideDuration is true, valid_until_iso remains null for backend auto-calculation

      const userIdToUse = selectedUser
        ? selectedUser.user_id
        : selectedUserForPass
          ? selectedUserForPass.id
          : null;

      if (!userIdToUse) {
        setAddPassError("Proszę wybrać użytkownika.");
        setIsAddingPass(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/pass_to_user/`,
        {
          pass_type_name: selectedPassType.name,
          user_id: userIdToUse,
          lesson_categories: lessonsCount,
          valid_from: valid_from_iso,
          valid_until: valid_until_iso,
          payed: payedValue,          // send checkbox value
          created_by_admin: true,     // always true
          // Include availability fields if they are configured
          ...(monthRestricted && {
            month_restricted: true,
            available_months: availableMonths,
          }),
          ...(purchasePeriodRestricted && {
            purchase_period_restricted: true,
            purchase_available_from: purchaseAvailableFrom ? new Date(purchaseAvailableFrom).toISOString() : null,
            purchase_available_until: purchaseAvailableUntil ? new Date(purchaseAvailableUntil).toISOString() : null,
          }),
        },
        {
          headers: {
            "Access-Token": process.env.REACT_APP_API_TOKEN,
          },
        }
      );

      if (response.status === 200) {
        setAddPassSuccess("Karnet został dodany pomyślnie!");
        setAddPassError("");

        // Update the selectedUser.passes with the new pass if selectedUser is set
        if (selectedUser && selectedUser.user_id === userIdToUse) {
          setSelectedUser((prev) => ({
            ...prev,
            passes: [...prev.passes, response.data],
          }));

          // Update usersWithPasses state
          setUsersWithPasses((prevUsers) =>
            prevUsers.map((user) =>
              user.user_id === selectedUser.user_id
                ? {
                  ...user,
                  passes: [...user.passes, response.data],
                }
                : user
            )
          );
        }

        handleCloseAddPassDialog();
      } else {
        setAddPassError("Nie udało się dodać karnetu.");
        setAddPassSuccess("");
      }
    } catch (error) {
      console.error("Failed to add pass:", error);
      setAddPassError("Nie udało się dodać karnetu.");
    } finally {
      setIsAddingPass(false);
    }
  };

  const handleCloseAddPassDialog = () => {
    setAddPassDialogOpen(false);
    setSelectedPassType(null);
    setLessonCategoryCounts({});
    setAddPassError("");
    setAddPassSuccess("");
    setDurationValue("");
    setDurationUnit("days");
    setSelectedUserForPass(null);
    setPayedValue(true); // reset checkbox to default
    setShouldHideDuration(false); // reset duration field visibility
    // Reset availability settings
    setMonthRestricted(false);
    setAvailableMonths([]);
    setPurchasePeriodRestricted(false);
    setPurchaseAvailableFrom("");
    setPurchaseAvailableUntil("");
  };

  // Function to fetch all users
  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user`,
        {
          headers: { "Access-Token": process.env.REACT_APP_API_TOKEN },
        }
      );

      if (response.status === 200 && Array.isArray(response.data.objects)) {
        setAllUsers(response.data.objects);
      } else {
        throw new Error("Failed to fetch users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // Function to open Add Pass Dialog and fetch users if needed
  const handleOpenAddPassDialog = () => {
    setAddPassDialogOpen(true);
    if (allUsers.length === 0) {
      fetchAllUsers();
    }
  };

  // Updated userColumns to show both full name and email in the user list
  const userColumns = [
    {
      field: "full_name",
      headerName: "Użytkownik",
      flex: 1,
      minWidth: isMobile ? 200 : 300,
      renderCell: (params) => (
        <div>
          {params.row.full_name}
          <br />
          <span style={{ fontSize: isMobile ? "0.7em" : "0.8em", color: "#888" }}>
            {params.row.email}
          </span>
        </div>
      ),
    },
  ];

  const passColumns = [
    { 
      field: "pass_type_name", 
      headerName: "Typ Karnetu", 
      flex: 1,
      minWidth: isMobile ? 150 : 200
    },
    {
      field: "active",
      headerName: "Aktywny",
      flex: 0.5,
      minWidth: isMobile ? 80 : 100,
      renderCell: (params) => (params.value ? "Tak" : "Nie"),
    },
    {
      field: "payed",
      headerName: "Opłacony",
      flex: 0.5,
      minWidth: isMobile ? 80 : 100,
      renderCell: (params) => (params.value ? "Tak" : "Nie"),
    },
    {
      field: "actions",
      headerName: "Akcje",
      sortable: false,
      filterable: false,
      minWidth: isMobile ? 100 : 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            onClick={() => handleEditPass(params.row)}
            color="primary"
            size={isMobile ? "small" : "medium"}
          >
            <EditIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          <IconButton
            onClick={() => handleDeletePass(params.row)}
            color="error"
            size={isMobile ? "small" : "medium"}
          >
            <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </Box>
      ),
    },
  ];

  const filteredUsers = usersWithPasses.filter((user) => {
    const nameMatches = user.full_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const hasPaidPass = user.passes.some((pass) => pass.payed);
    return nameMatches && (!showPaidUsersOnly || hasPaidPass);
  });

  return (
    <Box>
      {/* Header with Action Button */}
      <Box 
        display="flex" 
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between" 
        alignItems={{ xs: "stretch", sm: "center" }} 
        mb={3}
        gap={2}
      >
        <Box>
          <Typography 
            variant="h5" 
            component="h2" 
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
          >
            Karnety Użytkowników
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Zarządzaj karnetami użytkowników - dodawaj nowe, edytuj istniejące i monitoruj status płatności
          </Typography>
        </Box>
        <Button
          onClick={handleOpenAddPassDialog}
          color="primary"
          variant="contained"
          size="large"
          startIcon={<CardIcon />}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 2, sm: 3 },
            whiteSpace: 'nowrap',
            minWidth: { sm: 'fit-content' },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Dodaj Karnet Użytkownikowi
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            Dodaj Karnet
          </Box>
        </Button>
      </Box>

      {/* Users with Passes Section */}
      <Paper sx={{ mb: 2, overflow: 'hidden' }}>
        <Box 
          onClick={handleUserPassesClick} 
          sx={{ 
            p: 2, 
            cursor: 'pointer', 
            backgroundColor: isUserListVisible ? '#f5f5f5' : 'white',
            borderBottom: isUserListVisible ? '1px solid #e0e0e0' : 'none',
            '&:hover': {
              backgroundColor: '#f8f9fa'
            },
            transition: 'background-color 0.2s ease'
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <PeopleIcon sx={{ mr: 2, color: '#667eea' }} />
              <Typography variant="h6">
                Użytkownicy z aktywnymi karnetami
              </Typography>
            </Box>
            <IconButton size="small">
              {isUserListVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {isUserListVisible && (
          <Box sx={{ p: 2 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
                <Typography ml={2}>Ładowanie danych...</Typography>
              </Box>
            ) : usersWithPasses.length > 0 ? (
              <>
                <Box mb={2}>
                  <TextField
                    label="Szukaj użytkownika"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                    margin="normal"
                    sx={{ mb: 2 }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showPaidUsersOnly}
                        onChange={(e) => setShowPaidUsersOnly(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Pokaż tylko użytkowników z opłaconym karnetem"
                  />
                </Box>

                <Paper elevation={2} sx={{ height: { xs: 400, sm: 500 }, width: "100%" }}>
                  <DataGrid
                    rows={filteredUsers}
                    columns={userColumns}
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: isMobile ? 5 : 10 },
                      },
                    }}
                    pageSizeOptions={isMobile ? [5, 10] : [10, 25, 50]}
                    getRowId={(row) => row.user_id}
                    onRowClick={(params) => handleUserClick(params.row)}
                    sx={{
                      '& .MuiDataGrid-cell:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                      '& .MuiDataGrid-row:hover': {
                        backgroundColor: '#f8f9fa',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#f5f5f5',
                      },
                      '& .MuiDataGrid-cell': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '6px 4px', sm: '16px 8px' },
                      },
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        fontWeight: 600,
                      },
                      '& .MuiDataGrid-footerContainer': {
                        minHeight: { xs: '40px', sm: '52px' }
                      }
                    }}
                    density={isMobile ? "compact" : "standard"}
                  />
                </Paper>
              </>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  Brak użytkowników z aktywnymi karnetami.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Dialog showing passes for the selected user */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Karnety użytkownika: {selectedUser?.full_name}
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {selectedUser?.email}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedUser?.passes && selectedUser.passes.length > 0 ? (
            <Paper style={{ height: isMobile ? 400 : 500, width: "100%" }}>
              <DataGrid
                rows={selectedUser.passes}
                columns={passColumns}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: isMobile ? 5 : 10 },
                  },
                }}
                pageSizeOptions={isMobile ? [5] : [5, 10, 25]}
                getRowId={(row) => row.id}
                density={isMobile ? "compact" : "standard"}
                sx={{
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '6px 4px', sm: '8px' },
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    fontWeight: 600,
                  }
                }}
              />
            </Paper>
          ) : (
            <Typography>Brak karnetów dla tego użytkownika.</Typography>
          )}
          {/* Add Pass Button */}
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              onClick={() => setAddPassDialogOpen(true)}
              color="primary"
              variant="contained"
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Dodaj Karnet
            </Button>
          </Box>
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            onClick={handleCloseDialog} 
            color="error" 
            variant={isMobile ? "contained" : "text"}
            fullWidth={isMobile}
          >
            Zamknij
          </Button>
        </Box>
      </Dialog>

      {/* Dialog for adding pass */}
      <Dialog
        open={addPassDialogOpen}
        onClose={handleCloseAddPassDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Dodaj nowy karnet
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          {/* User Selection */}
          {!selectedUser && (
            <Autocomplete
              options={allUsers}
              getOptionLabel={(option) =>
                `${option.full_name} (${option.email})`
              }
              onChange={(event, newValue) => {
                setSelectedUserForPass(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Wybierz Użytkownika"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                />
              )}
            />
          )}

          {/* Pass Type Selection */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="pass-select-label">Wybierz karnet</InputLabel>
            <Select
              labelId="pass-select-label"
              value={selectedPassType}
              onChange={handlePassTypeChange}
              label="Wybierz karnet"
            >
              {passTypes.map((pass) => (
                <MenuItem key={pass.id} value={pass}>
                  {pass.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Lesson Categories */}
          {selectedPassType &&
            selectedPassType.lesson_categories_for_pass_type.map((category) => (
              <FormControl
                fullWidth
                sx={{ mb: 2 }}
                key={category.lesson_category_id}
              >
                <TextField
                  label={`${category.is_unlimited ? 'Nieograniczone lekcje' : 'Liczba lekcji'} dla kategorii: ${getLessonCategoryName(
                    category.lesson_category_id
                  )}`}
                  type={category.is_unlimited ? "text" : "number"}
                  variant="outlined"
                  value={
                    category.is_unlimited ? "Unlimited" : (lessonCategoryCounts[category.lesson_category_id] || "")
                  }
                  onChange={(e) => {
                    if (!category.is_unlimited) {
                      handleLessonCountChange(
                        category.lesson_category_id,
                        e.target.value
                      );
                    }
                  }}
                  fullWidth
                  InputProps={{
                    readOnly: category.is_unlimited || false,
                  }}
                  helperText={category.is_unlimited ? "Ten karnet oferuje nieograniczone lekcje dla tej kategorii" : ""}
                />
              </FormControl>
            ))}

          {/* Duration Selection - conditionally hidden */}
          {!shouldHideDuration ? (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <TextField
                  label="Długość trwania"
                  type="number"
                  variant="outlined"
                  value={durationValue}
                  onChange={(e) => setDurationValue(e.target.value)}
                  inputProps={{ min: 1 }}
                  fullWidth
                />
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="duration-unit-label">Jednostka</InputLabel>
                <Select
                  labelId="duration-unit-label"
                  value={durationUnit}
                  onChange={(e) => setDurationUnit(e.target.value)}
                  label="Jednostka"
                >
                  <MenuItem value="days">Dni</MenuItem>
                  <MenuItem value="weeks">Tygodnie</MenuItem>
                  <MenuItem value="months">Miesiące</MenuItem>
                </Select>
              </FormControl>
            </>
          ) : (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                ℹ️ Czas ważności zostanie automatycznie obliczony na podstawie ustawień typu karnetu
                {selectedPassType?.month_restricted && (
                  <span> (ograniczenie miesięczne)</span>
                )}
                {selectedPassType?.purchase_period_restricted && (
                  <span> (ograniczenie okresu zakupu)</span>
                )}
              </Typography>
            </Box>
          )}

          {/* Month Availability Section */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={monthRestricted}
                  onChange={(e) => setMonthRestricted(e.target.checked)}
                  color="primary"
                />
              }
              label="Ograniczenie dostępności do konkretnych miesięcy"
            />

            {monthRestricted && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Wybierz dostępne miesiące:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {monthNames.map((monthName, index) => (
                    <FormControlLabel
                      key={index + 1}
                      control={
                        <Checkbox
                          size="small"
                          checked={availableMonths.includes(index + 1)}
                          onChange={() => handleMonthToggle(index + 1)}
                          color="primary"
                        />
                      }
                      label={monthName}
                      sx={{ fontSize: '0.875rem' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Purchase Period Availability Section */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={purchasePeriodRestricted}
                  onChange={(e) => setPurchasePeriodRestricted(e.target.checked)}
                  color="primary"
                />
              }
              label="Ograniczenie okresu zakupu"
            />

            {purchasePeriodRestricted && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  label="Dostępny od"
                  type="datetime-local"
                  variant="outlined"
                  value={purchaseAvailableFrom}
                  onChange={(e) => setPurchaseAvailableFrom(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  label="Dostępny do"
                  type="datetime-local"
                  variant="outlined"
                  value={purchaseAvailableUntil}
                  onChange={(e) => setPurchaseAvailableUntil(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Payed checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={payedValue}
                onChange={(e) => setPayedValue(e.target.checked)}
                color="primary"
              />
            }
            label="Opłacony"
          />

          {addPassError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {addPassError}
            </Alert>
          )}
          {addPassSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {addPassSuccess}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 }, p: 2 }}>
          <Button 
            onClick={handleCloseAddPassDialog} 
            color="error"
            fullWidth={isMobile}
            variant={isMobile ? "outlined" : "text"}
          >
            Anuluj
          </Button>
          <Button
            onClick={handleAddPass}
            color="primary"
            variant="contained"
            fullWidth={isMobile}
            disabled={
              isAddingPass ||
              !selectedPassType ||
              // Only require duration fields when they're visible (not auto-calculated)
              (!shouldHideDuration && (!durationValue || !durationUnit || parseInt(durationValue, 10) <= 0)) ||
              (!selectedUser && !selectedUserForPass)
            }
          >
            {isAddingPass ? <CircularProgress size={24} /> : "Dodaj Karnet"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for editing pass */}
      <Dialog
        open={editPassDialogOpen}
        onClose={() => setEditPassDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Edytuj Karnet
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          {/* Active Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={editingPass?.active || false}
                onChange={(e) =>
                  setEditingPass((prev) => ({
                    ...prev,
                    active: e.target.checked,
                  }))
                }
                color="primary"
              />
            }
            label="Aktywny"
          />

          {/* Pass Type Name */}
          <TextField
            label="Typ Karnetu"
            variant="outlined"
            value={editingPass?.pass_type_name || ""}
            onChange={(e) =>
              setEditingPass((prev) => ({
                ...prev,
                pass_type_name: e.target.value,
              }))
            }
            fullWidth
            margin="normal"
          />

          {/* Valid Until */}
          <TextField
            label="Ważny do"
            variant="outlined"
            type="date"
            value={
              editingPass?.valid_until
                ? editingPass.valid_until.split("T")[0]
                : ""
            }
            onChange={(e) => {
              const dateValue = e.target.value;
              setEditingPass((prev) => ({
                ...prev,
                valid_until: dateValue,
              }));
            }}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
          />

          {/* Editing lesson categories */}
          {editingPass?.lesson_categories_for_pass &&
            editingPass.lesson_categories_for_pass.length > 0 && (
              <Box mt={2}>
                <Typography variant="h6">Kategorie Lekcji</Typography>
                {editingPass.lesson_categories_for_pass.map(
                  (category, index) => (
                    <Box key={index} mt={1}>
                      <TextField
                        label="Nazwa Kategorii"
                        variant="outlined"
                        value={category.name || ""}
                        fullWidth
                        margin="normal"
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                      <TextField
                        label="Pozostałe Lekcje"
                        variant="outlined"
                        type={category.is_unlimited ? "text" : "number"}
                        value={category.is_unlimited ? "Nieograniczone" : category.remaining_lessons}
                        onChange={(e) => {
                          if (!category.is_unlimited) {
                            const newRemaining = parseInt(e.target.value, 10);
                            if (!isNaN(newRemaining)) {
                              setEditingPass((prev) => {
                                const newCategories = [
                                  ...prev.lesson_categories_for_pass,
                                ];
                                newCategories[index].remaining_lessons =
                                  newRemaining;
                                return {
                                  ...prev,
                                  lesson_categories_for_pass: newCategories,
                                };
                              });
                            }
                          }
                        }}
                        fullWidth
                        margin="normal"
                        InputProps={{
                          readOnly: category.is_unlimited || false,
                        }}
                        helperText={category.is_unlimited ? "Ten karnet oferuje nieograniczone lekcje" : ""}
                      />
                      <TextField
                        label="Zużyte Lekcje"
                        variant="outlined"
                        type="number"
                        value={category.consumed_lessons}
                        onChange={(e) => {
                          const newConsumed = parseInt(e.target.value, 10);
                          if (!isNaN(newConsumed)) {
                            setEditingPass((prev) => {
                              const newCategories = [
                                ...prev.lesson_categories_for_pass,
                              ];
                              newCategories[index].consumed_lessons =
                                newConsumed;
                              return {
                                ...prev,
                                lesson_categories_for_pass: newCategories,
                              };
                            });
                          }
                        }}
                        fullWidth
                        margin="normal"
                      />
                    </Box>
                  )
                )}
              </Box>
            )}
        </DialogContent>
        <Box 
          display="flex" 
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="flex-end" 
          alignItems="stretch" 
          gap={{ xs: 1, sm: 0 }}
          p={2}
        >
          <Button
            onClick={() => setEditPassDialogOpen(false)}
            color="error"
            variant="outlined"
            disabled={isUpdating}
            fullWidth={isMobile}
            sx={{ mr: { sm: 1 } }}
          >
            Anuluj
          </Button>
          <Button
            onClick={handleSaveEditPass}
            color="primary"
            variant="contained"
            disabled={isUpdating}
            fullWidth={isMobile}
          >
            {isUpdating ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Zapisz"
            )}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default UserPassesComponent;
